import React, { useState, useEffect, useRef, useCallback } from "react";
import { Input, Button, Badge, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { GetData, PostData } from "../../services/api";

const fmtTime = (d) => {
  if (!d) return "";
  const t = new Date(d);
  const hh = t.getHours() % 12 || 12;
  const mm = String(t.getMinutes()).padStart(2, "0");
  return `${hh}:${mm} ${t.getHours() >= 12 ? "p.m." : "a.m."}`;
};

const fmtDay = (d) => {
  if (!d) return "";
  const t = new Date(d);
  const today = new Date();
  const yst = new Date(); yst.setDate(today.getDate() - 1);
  const same = (a, b) => a.toDateString() === b.toDateString();
  if (same(t, today)) return "Hoy";
  if (same(t, yst)) return "Ayer";
  return t.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
};

// Color estable a partir de un texto (para avatares).
const AV_COLORS = ["#e9553e", "#0d9488", "#6366f1", "#0284c7", "#d97706", "#7c3aed", "#059669", "#db2777"];
const avatarColor = (s) => {
  const str = String(s || "?");
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return AV_COLORS[h % AV_COLORS.length];
};
const initials = (name, phone) => {
  const src = (name || "").trim();
  if (src) {
    const parts = src.split(/\s+/);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase() || src[0].toUpperCase();
  }
  const p = String(phone || "").replace(/\D/g, "");
  return p.slice(-2) || "#";
};

const Avatar = ({ name, phone, size = 44 }) => (
  <div style={{
    width: size, height: size, borderRadius: "50%", flex: `0 0 ${size}px`,
    background: avatarColor(name || phone), color: "#fff", fontWeight: 600,
    fontSize: size * 0.4, display: "flex", alignItems: "center", justifyContent: "center",
    letterSpacing: 0.5, userSelect: "none",
  }}>{initials(name, phone)}</div>
);

const Conversations = () => {
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // conversation object
  const [thread, setThread] = useState([]); // messages
  const [q, setQ] = useState("");
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplId, setTplId] = useState("");
  const [tplVars, setTplVars] = useState([]);
  const [muted, setMuted] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("wa_muted") || "[]")); } catch { return new Set(); }
  });
  const bottomRef = useRef(null);
  const scrollRef = useRef(null);
  const selectedIdRef = useRef(null);
  const mutedRef = useRef(muted);
  const lastMsgIdRef = useRef(null);

  // Auto-scroll cuando aparece un mensaje nuevo (entrante o saliente).
  useEffect(() => {
    const last = thread[thread.length - 1];
    if (last && last.id !== lastMsgIdRef.current) {
      lastMsgIdRef.current = last.id;
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    }
  }, [thread]);

  useEffect(() => { selectedIdRef.current = selected?.id || null; }, [selected]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);

  const isMuted = (id) => mutedRef.current.has(String(id));
  const toggleMute = (id) => {
    setMuted((prev) => {
      const next = new Set(prev);
      const k = String(id);
      if (next.has(k)) next.delete(k); else next.add(k);
      try { localStorage.setItem("wa_muted", JSON.stringify([...next])); } catch (_) {}
      return next;
    });
  };

  const inWindow = selected?.windowExpiresAt && new Date(selected.windowExpiresAt).getTime() > Date.now();
  const lastTypingRef = useRef(0);

  // Al teclear, muestra "escribiendo…" en el WhatsApp del cliente (throttle 8s).
  const onTypeChange = (v) => {
    setText(v);
    if (!selected || !inWindow) return;
    const now = Date.now();
    if (now - lastTypingRef.current > 8000) {
      lastTypingRef.current = now;
      PostData(`/whatsapp/conversations/${selected.id}/typing`, {}).catch(() => {});
    }
  };

  const qRef = useRef(q);
  useEffect(() => { qRef.current = q; }, [q]);

  const loadThread = useCallback(async (id, { markRead = false } = {}) => {
    try {
      const r = await GetData(`/whatsapp/conversations/${id}/messages`);
      const conv = r?.data?.conversation || null;
      setThread(r?.data?.messages || []);
      setSelected(conv);
      // Solo marca leído si de verdad hay no-leídos (evita el POST /read innecesario).
      if (markRead && conv && conv.unreadCount > 0) {
        PostData(`/whatsapp/conversations/${id}/read`, {}).catch(() => {});
      }
    } catch (e) { /* ignore */ }
  }, []);

  const openConv = useCallback((c) => { setText(""); setTplId(""); setTplVars([]); loadThread(c.id, { markRead: true }); }, [loadThread]);

  const loadConvs = useCallback(async () => {
    try {
      const r = await GetData(`/whatsapp/conversations${qRef.current ? `?q=${encodeURIComponent(qRef.current)}` : ""}`);
      setConvs(r?.data?.items || []);
    } catch (e) { /* ignore */ }
  }, []);

  // Maneja un evento de tiempo real (SSE): refresca lista/hilo y notifica.
  const onRealtime = useCallback((evt) => {
    if (evt?.type !== "message") return;
    loadConvs();
    const openId = selectedIdRef.current;
    if (openId && openId === evt.conversationId) loadThread(openId, { markRead: !document.hidden });
    if (evt.direction === "in" && !isMuted(evt.conversationId)) {
      const activeFocused = openId === evt.conversationId && !document.hidden;
      if (!activeFocused && typeof Notification !== "undefined" && Notification.permission === "granted") {
        try {
          const n = new Notification(`WhatsApp · ${evt.contactName || evt.contactPhone}`, { body: evt.preview || "", tag: String(evt.conversationId) });
          n.onclick = () => { window.focus(); loadThread(evt.conversationId, { markRead: true }); n.close(); };
        } catch (_) {}
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConvs, loadThread]);

  useEffect(() => {
    if (typeof Notification !== "undefined" && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    (async () => { await loadConvs(); setLoading(false); })();
    GetData("/templates/whatsapp").then((r) => setTemplates((r?.data || []).filter((t) => String(t.status || "").toUpperCase() === "APPROVED"))).catch(() => {});

    // Tiempo real vía SSE (sin polling). El token va por query (EventSource no manda headers).
    let token = "";
    try { token = JSON.parse(localStorage.getItem("authUser") || "{}").token || ""; } catch (_) {}
    const base = process.env.REACT_APP_API || "";
    const url = base ? `${base}/whatsapp/stream?token=${encodeURIComponent(token)}` : `/v1/whatsapp/stream?token=${encodeURIComponent(token)}`;
    let es;
    try {
      es = new EventSource(url);
      es.onmessage = (e) => { try { onRealtime(JSON.parse(e.data)); } catch (_) {} };
      es.onerror = () => { /* EventSource reconecta solo */ };
    } catch (_) {}
    // Respaldo por si la conexión SSE se cae: refresca la lista cada 60s (solo visible).
    const safety = setInterval(() => { if (!document.hidden) loadConvs(); }, 60000);
    return () => { try { es && es.close(); } catch (_) {} clearInterval(safety); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadConvs, onRealtime]);

  // Respaldo del HILO ABIERTO: si el SSE no llega (buffering del proxy, etc.), refresca
  // la conversación abierta cada 8s mientras esté visible. Así la caja se actualiza sola.
  useEffect(() => {
    const id = selected?.id;
    if (!id) return;
    const t = setInterval(() => {
      if (!document.hidden) loadThread(id, { markRead: !document.hidden });
    }, 8000);
    return () => clearInterval(t);
  }, [selected?.id, loadThread]);

  const bodyOfTpl = (t) => ((t?.params?.components || []).find((c) => String(c.type).toUpperCase() === "BODY") || {}).text || "";
  const onPickTemplate = (id) => {
    setTplId(id);
    const t = templates.find((x) => String(x.id) === String(id));
    const n = (bodyOfTpl(t).match(/\{\{\s*\d+\s*\}\}/g) || []).length;
    setTplVars(Array(n).fill(""));
  };

  const send = async () => {
    if (!selected) return;
    const usingTpl = !inWindow || tplId;
    if (usingTpl && !tplId) return toast.error("Fuera de la ventana de 24h: elige una plantilla.");
    if (!usingTpl && !text.trim()) return;
    setSending(true);
    try {
      const payload = usingTpl ? { templateId: tplId, variables: tplVars } : { text: text.trim() };
      const r = await PostData(`/whatsapp/conversations/${selected.id}/reply`, payload);
      if (r?.data?.ok) {
        setText(""); setTplId(""); setTplVars([]);
        await loadThread(selected.id);
        await loadConvs();
      } else toast.error("No se pudo enviar.");
    } catch (e) {
      toast.error(e?.response?.data?.error?.message || e?.response?.data?.message || "No se pudo enviar.");
    } finally { setSending(false); }
  };

  // Modo automático (bot/n8n) de la conversación. undefined = ON (por defecto).
  const autoOn = selected ? selected.autoBot !== false : false;
  const toggleAuto = async () => {
    if (!selected) return;
    const next = !autoOn;
    setSelected((s) => (s ? { ...s, autoBot: next } : s)); // optimista
    try {
      await PostData(`/whatsapp/conversations/${selected.id}/auto`, { auto: next });
    } catch (e) {
      setSelected((s) => (s ? { ...s, autoBot: !next } : s)); // revertir
      toast.error("No se pudo cambiar el modo automático.");
    }
  };

  // Agrupa mensajes por día para insertar separadores de fecha.
  const rows = [];
  let lastDay = null;
  for (const m of thread) {
    const day = m.timestamp ? new Date(m.timestamp).toDateString() : "";
    if (day !== lastDay) { rows.push({ sep: true, id: `sep-${day}`, label: fmtDay(m.timestamp) }); lastDay = day; }
    rows.push(m);
  }

  const statusIcon = (s) => {
    const st = String(s || "").toLowerCase();
    if (st === "read") return { icon: "ri-check-double-line", color: "#53bdeb" };
    if (st === "delivered") return { icon: "ri-check-double-line", color: "#8696a0" };
    if (st === "sent") return { icon: "ri-check-line", color: "#8696a0" };
    if (st === "failed") return { icon: "ri-error-warning-line", color: "#ef4444" };
    return null;
  };

  return (
    <div className="page-content" style={{ paddingBottom: 0 }}>
      <style>{`
        .wa-shell{--wa-bg:#fff;--wa-panel:#f0f2f5;--wa-border:#e9edef;--wa-text:#111b21;--wa-sub:#667781;}
        :root[data-theme="dark"] .wa-shell,:root:not([data-theme="light"]) .wa-shell{}
        .wa-conv-row:hover{background:#f5f6f6;}
        .wa-scroll::-webkit-scrollbar{width:8px;}
        .wa-scroll::-webkit-scrollbar-thumb{background:rgba(0,0,0,.18);border-radius:8px;}
        .wa-list::-webkit-scrollbar{width:8px;}
        .wa-list::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:8px;}
        .wa-input:focus{box-shadow:none;border-color:#25d366;}
      `}</style>

      <div className="wa-shell" style={{
        display: "flex", height: "calc(100vh - 130px)", border: "1px solid #e9edef",
        borderRadius: 14, overflow: "hidden", background: "#fff",
        boxShadow: "0 6px 24px rgba(11,20,26,.06)",
      }}>
        {/* ---------- Lista ---------- */}
        <div style={{ width: 380, flex: "0 0 380px", borderRight: "1px solid #e9edef", display: "flex", flexDirection: "column", background: "#fff" }}>
          <div style={{ padding: "16px 18px 10px" }}>
            <h4 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111b21" }}>Conversaciones</h4>
            <div style={{ fontSize: 13, color: "#667781", marginTop: 2 }}>WhatsApp Business</div>
          </div>
          <div style={{ padding: "0 14px 10px" }}>
            <div style={{ position: "relative" }}>
              <i className="ri-search-line" style={{ position: "absolute", left: 12, top: 10, color: "#8696a0", fontSize: 16 }} />
              <Input className="wa-input" placeholder="Buscar por nombre o teléfono…" value={q}
                onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadConvs()}
                style={{ paddingLeft: 36, height: 40, borderRadius: 22, background: "#f0f2f5", border: "1px solid transparent", fontSize: 14.5 }} />
            </div>
          </div>

          <div className="wa-list" style={{ flex: 1, overflowY: "auto" }}>
            {loading ? (
              <div style={{ padding: 24, textAlign: "center" }}><Spinner size="sm" /></div>
            ) : convs.length === 0 ? (
              <div style={{ padding: 24, color: "#667781", textAlign: "center", fontSize: 14 }}>Sin conversaciones todavía.</div>
            ) : convs.map((c) => {
              const active = selected?.id === c.id;
              const unread = (c.unreadCount || 0) > 0;
              return (
                <div key={c.id} onClick={() => openConv(c)} className="wa-conv-row"
                  style={{
                    display: "flex", gap: 12, alignItems: "center", padding: "11px 16px", cursor: "pointer",
                    borderLeft: active ? "3px solid #25d366" : "3px solid transparent",
                    background: active ? "#f0fdf4" : "transparent",
                  }}>
                  <Avatar name={c.contactName} phone={c.contactPhone} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                      <span style={{ fontWeight: unread ? 800 : 600, fontSize: 15.5, color: "#111b21", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.contactName || c.contactPhone}
                      </span>
                      <span style={{ fontSize: 12, fontWeight: unread ? 700 : 400, color: unread ? "#25d366" : "#8696a0", flex: "0 0 auto" }}>{fmtTime(c.lastMessageAt)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 3 }}>
                      <span style={{ fontSize: 13.5, fontWeight: unread ? 700 : 400, color: unread ? "#111b21" : "#667781", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {c.lastDirection === "out" ? "↩ " : ""}{c.lastMessageText || ""}
                      </span>
                      <span style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: 4 }}>
                        {isMuted(c.id) && <i className="ri-notification-off-line" style={{ color: "#8696a0", fontSize: 14 }} />}
                        {c.unreadCount > 0 && (
                          <Badge pill style={{ background: isMuted(c.id) ? "#8696a0" : "#25d366", fontSize: 11.5, minWidth: 20, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
                            {c.unreadCount}
                          </Badge>
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ---------- Hilo ---------- */}
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
          {!selected ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", color: "#8696a0", background: "#f7f9fa" }}>
              <i className="ri-whatsapp-line" style={{ fontSize: 64, color: "#25d366", opacity: .5 }} />
              <div style={{ marginTop: 12, fontSize: 16 }}>Elige una conversación para empezar</div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 18px", borderBottom: "1px solid #e9edef", background: "#f0f2f5" }}>
                <Avatar name={selected.contactName} phone={selected.contactPhone} size={42} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "#111b21", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selected.contactName || selected.contactPhone}
                  </div>
                  <div style={{ fontSize: 13, color: "#667781" }}>
                    {selected.contactPhone}
                    {!inWindow && <span style={{ marginLeft: 8, color: "#b45309", fontWeight: 600 }}>· Fuera de la ventana de 24h</span>}
                  </div>
                </div>
                <button type="button" onClick={toggleAuto}
                  title={autoOn ? "Automático activado: los mensajes también van a la automatización (n8n). Clic para atender tú." : "Manual: tú atiendes esta conversación. Clic para reactivar la automatización."}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                    border: "1px solid " + (autoOn ? "#25d366" : "#d1d7db"), background: autoOn ? "#eafff1" : "#fff",
                    color: autoOn ? "#0b8043" : "#667781", borderRadius: 20, padding: "5px 12px", fontSize: 13, fontWeight: 600,
                  }}>
                  <i className={autoOn ? "ri-robot-2-fill" : "ri-user-3-line"} style={{ fontSize: 16 }} />
                  {autoOn ? "Automático" : "Manual"}
                  <span style={{
                    width: 30, height: 16, borderRadius: 10, background: autoOn ? "#25d366" : "#c4ccd1",
                    position: "relative", transition: "background .15s", flex: "0 0 30px",
                  }}>
                    <span style={{
                      position: "absolute", top: 2, left: autoOn ? 16 : 2, width: 12, height: 12,
                      borderRadius: "50%", background: "#fff", transition: "left .15s",
                    }} />
                  </span>
                </button>
                <Button color="link" style={{ color: isMuted(selected.id) ? "#8696a0" : "#54656f", fontSize: 20, padding: 6 }}
                  title={isMuted(selected.id) ? "Silenciada — clic para activar notificaciones" : "Silenciar notificaciones de esta conversación"}
                  onClick={() => toggleMute(selected.id)}>
                  <i className={isMuted(selected.id) ? "ri-notification-off-line" : "ri-notification-3-line"} />
                </Button>
              </div>

              {/* Mensajes */}
              <div ref={scrollRef} className="wa-scroll" style={{
                flex: 1, overflowY: "auto", padding: "18px 8%",
                background: "#efeae2",
                backgroundImage: "radial-gradient(rgba(0,0,0,.035) 1px, transparent 0)", backgroundSize: "22px 22px",
              }}>
                {rows.map((m) => m.sep ? (
                  <div key={m.id} style={{ textAlign: "center", margin: "14px 0" }}>
                    <span style={{ background: "#ffffffcc", color: "#54656f", fontSize: 12.5, padding: "5px 12px", borderRadius: 8, boxShadow: "0 1px 1px rgba(0,0,0,.06)" }}>{m.label}</span>
                  </div>
                ) : (
                  <div key={m.id} style={{ display: "flex", justifyContent: m.direction === "out" ? "flex-end" : "flex-start", marginBottom: 8 }}>
                    <div style={{
                      maxWidth: "70%", background: m.direction === "out" ? "#d9fdd3" : "#ffffff",
                      borderRadius: 10, padding: "8px 12px 6px", fontSize: 15, lineHeight: 1.4, color: "#111b21",
                      boxShadow: "0 1px 1px rgba(11,20,26,.13)", whiteSpace: "pre-wrap", wordBreak: "break-word",
                      borderTopRightRadius: m.direction === "out" ? 2 : 10, borderTopLeftRadius: m.direction === "out" ? 10 : 2,
                    }}>
                      {m.type === "template"
                        ? <span style={{ color: "#54656f" }}>{m.text || `[plantilla] ${m.templateName || ""}`}</span>
                        : m.text}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, marginTop: 3, color: "#667781", fontSize: 11.5 }}>
                        {m.via === "automation" && (
                          <span title="Enviado por la automatización (bot)" style={{ display: "inline-flex", alignItems: "center", gap: 2, color: "#0b8043", fontWeight: 600 }}>
                            <i className="ri-robot-2-fill" style={{ fontSize: 13 }} />bot
                          </span>
                        )}
                        <span>{fmtTime(m.timestamp)}</span>
                        {m.direction === "out" && statusIcon(m.status) && (
                          <i className={statusIcon(m.status).icon} style={{ color: statusIcon(m.status).color, fontSize: 15 }} />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                <div ref={bottomRef} />
              </div>

              {/* Responder */}
              <div style={{ padding: "12px 16px", borderTop: "1px solid #e9edef", background: "#f0f2f5" }}>
                {inWindow ? (
                  <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
                    <Input className="wa-input" type="textarea" rows={1} value={text} placeholder="Escribe un mensaje…"
                      onChange={(e) => onTypeChange(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                      style={{ borderRadius: 22, padding: "10px 16px", fontSize: 15, background: "#fff", border: "1px solid transparent", resize: "none", minHeight: 44 }} />
                    <Button onClick={send} disabled={sending}
                      style={{ borderRadius: "50%", width: 46, height: 46, flex: "0 0 46px", background: "#25d366", border: "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                      {sending ? <Spinner size="sm" style={{ color: "#fff" }} /> : <i className="ri-send-plane-2-fill" style={{ color: "#fff" }} />}
                    </Button>
                  </div>
                ) : (
                  <div>
                    <div style={{ color: "#b45309", marginBottom: 8, fontSize: 13.5, fontWeight: 500 }}>
                      <i className="ri-time-line me-1" />Fuera de la ventana de 24h: responde con una plantilla aprobada.
                    </div>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                      <Input type="select" value={tplId} onChange={(e) => onPickTemplate(e.target.value)}
                        style={{ maxWidth: 260, height: 42, fontSize: 14.5, borderRadius: 10 }}>
                        <option value="">Elegir plantilla…</option>
                        {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </Input>
                      <div style={{ flex: 1 }}>
                        {tplVars.map((v, i) => (
                          <Input key={i} className="mb-2" value={v} placeholder={`Valor {{${i + 1}}}`}
                            onChange={(e) => setTplVars(tplVars.map((x, idx) => (idx === i ? e.target.value : x)))}
                            style={{ height: 40, fontSize: 14.5, borderRadius: 10 }} />
                        ))}
                      </div>
                      <Button onClick={send} disabled={sending || !tplId}
                        style={{ height: 42, background: "#25d366", border: "none", fontSize: 15, fontWeight: 600, padding: "0 20px", borderRadius: 10 }}>
                        Enviar
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Conversations;
